import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  AccountInfo,
  BrowserAuthError,
  InteractionRequiredAuthError,
  PopupRequest,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { DEFAULT_AUTH_SCOPES, PASSWORD_RESET_SCOPES } from './auth.constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly scopes = [...DEFAULT_AUTH_SCOPES];
  private tokenRequest?: Promise<string | undefined>;
  private interactiveInProgress = false;

  constructor(private msalService: MsalService) {}

  login() {
    if (this.interactiveInProgress) {
      return;
    }
    this.interactiveInProgress = true;
    const loginRequest: RedirectRequest = {
      scopes: this.scopes,
    };
    if (this.isRunningInIframe()) {
      firstValueFrom(this.msalService.loginPopup(loginRequest as PopupRequest))
        .then((result) => {
          if (result?.account) {
            this.msalService.instance.setActiveAccount(result.account);
          }
        })
        .catch((error) => {
          console.error('[AuthService] Login popup failed', error);
        })
        .finally(() => {
          this.setInteractionCompleted();
        });
      return;
    }
    this.msalService.loginRedirect(loginRequest);
  }

  logout(): void {
    const account = this.getAccount();
    this.tokenRequest = undefined;
    this.interactiveInProgress = false;
    this.msalService.instance.setActiveAccount(null);
    const postLogoutRedirectUri =
      environment.azureActiveDirectory.postLogoutRedirectUri || window.location.origin;
    if (this.isRunningInIframe()) {
      firstValueFrom(
        this.msalService.logoutPopup({
          account: account ?? undefined,
          postLogoutRedirectUri,
          mainWindowRedirectUri: postLogoutRedirectUri,
        })
      ).catch((error) => {
        console.error('[AuthService] Logout popup failed', error);
      });
      return;
    }
    this.msalService.logoutRedirect({
      account: account ?? undefined,
      postLogoutRedirectUri,
    });
  }

  isLoggedIn(): boolean {
    return this.getAccount() !== null;
  }

  getCurrentAccount(): AccountInfo | null {
    return this.getAccount();
  }

  private getAccount(): AccountInfo | null {
    const active = this.msalService.instance.getActiveAccount();
    if (active) {
      return active;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      return accounts[0];
    }

    return null;
  }

  private async acquireTokenSilent(scopes: string[]): Promise<string | undefined> {
    const account = this.getAccount();
    if (!account) {
      if (!environment.production) {
        console.debug('[AuthService] No user account found');
      }
      return undefined;
    }

    const silentRequest: SilentRequest = {
      scopes,
      account,
    };

    try {
      const accessToken = await firstValueFrom(
        from(this.msalService.acquireTokenSilent(silentRequest)).pipe(
          map((response) => {
            if (!response.accessToken) {
              throw new Error('Token is undefined');
            }
            return response.accessToken;
          }),
          catchError((error) => {
            if (error instanceof InteractionRequiredAuthError) {
              console.warn('[AuthService] Interaction required to acquire token');
              this.startInteractiveAuth(silentRequest as RedirectRequest);
              return of(undefined);
            }
            if (error instanceof BrowserAuthError && error.errorCode === 'block_iframe_reload') {
              console.warn('[AuthService] MSAL blocked iframe reload during silent auth');
              return of(undefined);
            }
            if (error instanceof BrowserAuthError && error.errorCode === 'hash_empty_error') {
              console.warn('[AuthService] Ignoring transient empty hash during auth callback');
              return of(undefined);
            }
            console.error('[AuthService] Failed to acquire token silently', error);
            return of(undefined);
          })
        )
      );

      return accessToken;
    } catch (error: any) {
      console.error('[AuthService] Error getting access token', error);
      if (error.errorCode === 'interaction_in_progress') {
        console.warn('[AuthService] Interaction is currently in progress.');
        return undefined;
      }
      return undefined;
    }
  }

  public async getAccessToken(): Promise<string | undefined> {
    if (this.interactiveInProgress) {
      return undefined;
    }
    if (this.tokenRequest) {
      return this.tokenRequest;
    }
    this.tokenRequest = this.acquireTokenSilent(this.scopes).finally(() => {
      this.tokenRequest = undefined;
    });
    const token = await this.tokenRequest;
    if (!token) {
      if (!environment.production && this.isLoggedIn()) {
        console.warn('[AuthService] Failed to retrieve bearer token');
      }
      return undefined;
    }
    return token;
  }

  changePassword(): void {
    const passwordResetAuthority = this.resolvePasswordResetAuthority();
    if (!passwordResetAuthority) {
      if (!environment.production) {
        console.warn('[AuthService] Password reset policy not configured');
      }
      return;
    }
    if (this.interactiveInProgress) {
      return;
    }
    this.interactiveInProgress = true;
    const resetPasswordRequest: RedirectRequest = {
      authority: passwordResetAuthority,
      scopes: [...PASSWORD_RESET_SCOPES],
    };

    this.msalService.loginRedirect(resetPasswordRequest);
  }

  canChangePassword(): boolean {
    return !!this.resolvePasswordResetAuthority();
  }

  private startInteractiveAuth(request: RedirectRequest): void {
    if (this.interactiveInProgress) {
      return;
    }
    if (this.isRunningInIframe()) {
      this.interactiveInProgress = true;
      firstValueFrom(this.msalService.acquireTokenPopup(request as PopupRequest))
        .then((result) => {
          if (result?.account) {
            this.msalService.instance.setActiveAccount(result.account);
          }
        })
        .catch((error) => {
          console.error('[AuthService] Token popup failed', error);
        })
        .finally(() => {
          this.setInteractionCompleted();
        });
      return;
    }
    this.interactiveInProgress = true;
    this.msalService.acquireTokenRedirect(request);
  }

  setInteractionCompleted(): void {
    this.interactiveInProgress = false;
  }

  isInteractionInProgress(): boolean {
    return this.interactiveInProgress;
  }

  private isRunningInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }

  private resolvePasswordResetAuthority(): string | null {
    const aad = environment.azureActiveDirectory as {
      instance?: string;
      tenant?: string;
      passwordResetAuthority?: string;
      passwordResetPolicyName?: string;
    };

    if (aad?.passwordResetAuthority) {
      return aad.passwordResetAuthority;
    }

    if (aad?.passwordResetPolicyName && aad.instance && aad.tenant) {
      return `${aad.instance}${aad.tenant}${aad.passwordResetPolicyName}`;
    }

    return null;
  }
}
